import React, { useState } from 'react';
import { useDebug } from '../../contexts/DebugContext';

const DebugMenu = ({ 
  data, 
  title = "Debug Info", 
  position = "top-right",
  collapsible = true,
  maxHeight = "300px",
  maxWidth = "400px" 
}) => {
  const { isDebugVisible, isDebugAllowed } = useDebug();
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Debug logging
  console.log(`🔍 DebugMenu (${title}):`, { isDebugVisible, isDebugAllowed });

  // Don't render if debug is not visible or not allowed
  if (!isDebugVisible || !isDebugAllowed) {
    return null;
  }

  // Position styles
  const getPositionStyles = () => {
    const baseStyle = {
      position: 'fixed',
      zIndex: 9999,
      fontSize: '12px',
      fontFamily: 'monospace',
      borderRadius: '8px',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
    };

    switch (position) {
      case 'top-left':
        return { ...baseStyle, top: '10px', left: '10px' };
      case 'top-right':
        return { ...baseStyle, top: '10px', right: '10px' };
      case 'bottom-left':
        return { ...baseStyle, bottom: '10px', left: '10px' };
      case 'bottom-right':
        return { ...baseStyle, bottom: '10px', right: '10px' };
      case 'center':
        return { 
          ...baseStyle, 
          top: '50%', 
          left: '50%', 
          transform: 'translate(-50%, -50%)' 
        };
      default:
        return { ...baseStyle, top: '10px', right: '10px' };
    }
  };

  const handleToggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  return (
    <div style={getPositionStyles()}>
      <div
        style={{
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          color: '#00ff00',
          padding: '8px 12px',
          borderTopLeftRadius: '8px',
          borderTopRightRadius: '8px',
          borderBottom: isCollapsed ? 'none' : '1px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          minWidth: '200px',
          cursor: collapsible ? 'pointer' : 'default',
        }}
        onClick={collapsible ? handleToggleCollapse : undefined}
      >
        <span style={{ fontWeight: 'bold', fontSize: '11px' }}>
          {title}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {collapsible && (
            <span style={{ 
              fontSize: '10px', 
              color: '#888',
              userSelect: 'none'
            }}>
              {isCollapsed ? '▶' : '▼'}
            </span>
          )}
        </div>
      </div>
      
      {!isCollapsed && (
        <div
          style={{
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: '#ffffff',
            padding: '10px',
            maxHeight: maxHeight,
            maxWidth: maxWidth,
            overflowY: 'auto',
            overflowX: 'auto',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            minWidth: '200px',
          }}
        >
          {data ? (
            <pre style={{ 
              margin: 0, 
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              fontSize: '11px',
              lineHeight: '1.3'
            }}>
              {typeof data === 'string' ? data : JSON.stringify(data, null, 2)}
            </pre>
          ) : (
            <div style={{ color: '#888', fontStyle: 'italic' }}>
              No debug data available
            </div>
          )}
        </div>
      )}
      
      {/* Keyboard shortcut hint */}
      {!isCollapsed && (
        <div style={{
          position: 'absolute',
          bottom: '-20px',
          right: '0px',
          fontSize: '9px',
          color: '#666',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          padding: '2px 6px',
          borderRadius: '4px',
          whiteSpace: 'nowrap',
          pointerEvents: 'none'
        }}>
          {navigator.platform.toUpperCase().indexOf('MAC') >= 0 ? 'Cmd+Shift+D' : 'Ctrl+Shift+D'} to toggle
        </div>
      )}
    </div>
  );
};

// Additional debug components for specific use cases

export const DebugStateViewer = ({ state, title = "Component State", ...props }) => {
  return <DebugMenu data={state} title={title} {...props} />;
};

export const DebugPropsViewer = ({ props, title = "Component Props", ...otherProps }) => {
  return <DebugMenu data={props} title={title} {...otherProps} />;
};

export const DebugApiResponse = ({ response, title = "API Response", ...props }) => {
  return <DebugMenu data={response} title={title} {...props} />;
};

export const DebugConsole = ({ logs = [], title = "Debug Logs", maxEntries = 50, ...props }) => {
  const recentLogs = logs.slice(-maxEntries);
  const logText = recentLogs.map((log, index) => 
    `[${new Date().toLocaleTimeString()}] ${typeof log === 'object' ? JSON.stringify(log, null, 2) : log}`
  ).join('\n');
  
  return <DebugMenu data={logText} title={title} {...props} />;
};

export default DebugMenu;
